import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { isPlatformAuthenticatorAvailable, registerBiometric, authenticateBiometric } from '@/utils/webauthn';
import {
  isIosDespiaRuntime,
  triggerDespiaBioauth,
  generateDeviceSecret,
  saveDeviceSecret,
  loadDeviceSecret,
  clearDeviceSecret,
  warmupDespiaBridge,
} from '@/utils/despiaBioauth';
import { API_URL as API } from '@/utils/apiConfig';

// Per-device enrollment tracking. Biometric credentials are device-bound, so
// each device needs its own. WebAuthn (web/Android Chrome) and Despia native
// bioauth (iOS WKWebView in Despia) both store under the same backend
// `webauthn_credentials` array — auth_type field distinguishes them.
const DEVICE_ENROLLED_KEY = (userId) => `vault_device_biometric_${userId || 'anon'}`;

function getCurrentUserIdFromCache() {
  try {
    const cached = localStorage.getItem('eili_user_id');
    if (cached) return cached;
  } catch {}
  return null;
}

function isDeviceEnrolled(userId) {
  try {
    return !!localStorage.getItem(DEVICE_ENROLLED_KEY(userId));
  } catch {
    return false;
  }
}

function markDeviceEnrolled(userId) {
  try {
    localStorage.setItem(DEVICE_ENROLLED_KEY(userId), '1');
  } catch {}
}

function clearDeviceEnrolledFlag(userId) {
  try {
    localStorage.removeItem(DEVICE_ENROLLED_KEY(userId));
  } catch {}
}

/**
 * Custom hook encapsulating all Vault PIN / Biometric state and handlers.
 * Returns the state values, the VaultPinModal props, and helpers for sidebar integration.
 */
export default function useVault() {
  const [vaultModalVisible, setVaultModalVisible] = useState(false);
  const [vaultModalMode, setVaultModalMode] = useState('verify');
  const [vaultPinLoading, setVaultPinLoading] = useState(false);
  const [sidebarDefaultTab, setSidebarDefaultTab] = useState('chats');
  const [vaultHasBiometric, setVaultHasBiometric] = useState(false);  // server-side: any device enrolled
  const [biometricCapable, setBiometricCapable] = useState(false);

  useEffect(() => {
    // iOS Despia → must use the native bridge (WKWebView blocks WebAuthn).
    // Android Despia + web + Android Chrome → use WebAuthn directly.
    //
    // Do NOT gate on `isUserVerifyingPlatformAuthenticatorAvailable()`
    // returning true — that probe returns false in several legit cases
    // (no enrolled Face/Touch ID yet, certain WebView builds) and was
    // causing the "Enable on this device" button to disappear entirely.
    // We expose the option whenever the WebAuthn API exists at all and let
    // the real create() error bubble up so users see actionable feedback.
    if (isIosDespiaRuntime()) {
      setBiometricCapable(true);
      warmupDespiaBridge();
      return;
    }
    if (typeof window !== 'undefined' && window.PublicKeyCredential) {
      setBiometricCapable(true);
      isPlatformAuthenticatorAvailable().then((ok) => {
        if (!ok) console.warn('[biometric] platform authenticator probe returned false');
      });
    } else {
      setBiometricCapable(false);
    }
  }, []);

  // ── Despia-native biometric helpers ─────────────────────────────────────
  const enrollDespiaBioauth = async (userId) => {
    // 1) Confirm physical presence at this device
    await triggerDespiaBioauth('Enable biometric unlock for your vault');
    // 2) Generate secret + send to server
    const secret = generateDeviceSecret();
    await axios.post(
      `${API}/vault/bioauth/enroll`,
      { secret, device_label: '' },
      { withCredentials: true },
    );
    // 3) Persist the secret locally so future unlocks can re-send it
    saveDeviceSecret(userId, secret);
    markDeviceEnrolled(userId);
  };

  const unlockDespiaBioauth = async (userId) => {
    const secret = loadDeviceSecret(userId);
    if (!secret) throw new Error('not_enrolled');
    // 1) OS biometric prompt
    await triggerDespiaBioauth('Unlock vault');
    // 2) Server-side secret check
    await axios.post(`${API}/vault/bioauth/verify`, { secret }, { withCredentials: true });
  };

  // Listens for 'vault-request' events dispatched by the sidebar/components
  const handleVaultRequest = useCallback(async (e) => {
    const mode = e.detail?.mode || 'verify';

    if (mode === 'verify') {
      try {
        const statusRes = await axios.get(`${API}/vault/status`, { withCredentials: true });
        setVaultHasBiometric(!!statusRes.data.has_biometric);

        const userId = statusRes.data.user_id || getCurrentUserIdFromCache();
        const deviceHasBiometric = isDeviceEnrolled(userId);

        if (statusRes.data.has_biometric && biometricCapable && deviceHasBiometric) {
          try {
            if (isIosDespiaRuntime()) {
              await unlockDespiaBioauth(userId);
            } else {
              const pageOrigin = window.location.origin;
              const optRes = await axios.post(`${API}/vault/webauthn/auth-options`, { origin: pageOrigin }, { withCredentials: true });
              const assertion = await authenticateBiometric(optRes.data);
              await axios.post(`${API}/vault/webauthn/auth`, { ...assertion, origin: pageOrigin }, { withCredentials: true });
            }
            window.dispatchEvent(new CustomEvent('vault-unlocked'));
            toast.success('Vault unlocked');
            setSidebarDefaultTab('private');
            return { biometricSuccess: true };
          } catch (err) {
            // Only invalidate the local enrollment flag when the SERVER says
            // this device's credential is no longer recognised (HTTP 401).
            // User-initiated cancels, OS prompts dismissed, network blips,
            // not-recognised faces, etc. should all just fall through to PIN
            // without forcing a re-enrollment loop.
            const status = err?.response?.status;
            if (status === 401) {
              clearDeviceEnrolledFlag(userId);
              clearDeviceSecret(userId);
            }
          }
        }
      } catch {
        // Status check failed — fall through to PIN modal
      }
    }

    setVaultModalMode(mode);
    setVaultModalVisible(true);
    return { biometricSuccess: false };
  }, [biometricCapable]);

  const handleVaultPinVerify = async (pin) => {
    setVaultPinLoading(true);
    try {
      await axios.post(`${API}/vault/verify`, { pin }, { withCredentials: true });
      setVaultModalVisible(false);
      window.dispatchEvent(new CustomEvent('vault-unlocked'));
      toast.success('Vault unlocked');
      setSidebarDefaultTab('private');

      // Offer biometric enrollment ONLY if THIS device truly hasn't been
      // enrolled yet. We check three signals to avoid false re-prompts:
      //   1. localStorage flag (vault_device_biometric_<userId>)
      //   2. for Despia: presence of a stored bioauth secret
      //   3. server-side device count (if 0, definitely not enrolled)
      // The previous version only checked (1) which got cleared on any
      // transient biometric failure, causing a re-prompt loop.
      if (biometricCapable) {
        try {
          const statusRes = await axios.get(`${API}/vault/status`, { withCredentials: true });
          const userId = statusRes.data.user_id || getCurrentUserIdFromCache();
          const flag = isDeviceEnrolled(userId);
          const localSecret = isIosDespiaRuntime() ? !!loadDeviceSecret(userId) : false;

          let serverHasDevice = false;
          if (statusRes.data.has_biometric) {
            try {
              const dRes = await axios.get(`${API}/vault/webauthn/devices`, { withCredentials: true });
              serverHasDevice = (dRes.data?.devices || []).length > 0;
            } catch {}
          }

          // If we have a local secret but the flag was cleared, restore the flag
          if (localSecret && serverHasDevice && !flag) {
            markDeviceEnrolled(userId);
          }

          // If the local flag says we're enrolled but the SERVER reports
          // no credentials, the flag is stale (OS wiped the credential
          // or it was deleted from another device). Clear the flag so
          // the enrollment prompt can fire again.
          if (flag && !serverHasDevice && !localSecret) {
            try { localStorage.removeItem(`vault_device_biometric_${userId || 'anon'}`); } catch {}
          }

          // Skip the prompt only when we have BOTH a local-side signal
          // AND a matching server-side credential. Anything else means
          // this device cannot biometric-unlock today, so we should
          // surface the enrollment CTA.
          const alreadyEnrolledHere =
            (flag && serverHasDevice) || (localSecret && serverHasDevice);
          if (!alreadyEnrolledHere) {
            setTimeout(() => {
              setVaultModalMode('enroll_biometric');
              setVaultModalVisible(true);
            }, 1200);
          }
        } catch {
          // Best-effort fallback: only prompt if no biometric exists anywhere
          if (!vaultHasBiometric) {
            setTimeout(() => {
              setVaultModalMode('enroll_biometric');
              setVaultModalVisible(true);
            }, 1200);
          }
        }
      }
    } catch {
      toast.error('Invalid PIN');
    }
    setVaultPinLoading(false);
  };

  const handleVaultPinSetup = async (pin, password) => {
    setVaultPinLoading(true);
    try {
      if (password) {
        await axios.post(`${API}/vault/reset`, { password, new_pin: pin }, { withCredentials: true });
        toast.success('PIN reset successfully');
      } else {
        await axios.put(`${API}/vault/setup`, { pin }, { withCredentials: true });
        toast.success('Vault PIN created!');
      }
      setVaultModalVisible(false);
      window.dispatchEvent(new CustomEvent('vault-unlocked'));
      setSidebarDefaultTab('private');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setVaultPinLoading(false);
  };

  const handleVaultBiometricAuth = async () => {
    setVaultPinLoading(true);
    try {
      const userId = getCurrentUserIdFromCache();
      if (isIosDespiaRuntime()) {
        await unlockDespiaBioauth(userId);
      } else {
        const pageOrigin = window.location.origin;
        const optRes = await axios.post(`${API}/vault/webauthn/auth-options`, { origin: pageOrigin }, { withCredentials: true });
        const assertion = await authenticateBiometric(optRes.data);
        await axios.post(`${API}/vault/webauthn/auth`, { ...assertion, origin: pageOrigin }, { withCredentials: true });
      }
      setVaultModalVisible(false);
      window.dispatchEvent(new CustomEvent('vault-unlocked'));
      toast.success('Vault unlocked');
      setSidebarDefaultTab('private');
    } catch {
      throw new Error('Biometric auth failed');
    } finally {
      setVaultPinLoading(false);
    }
  };

  const handleVaultBiometricEnroll = async () => {
    try {
      let userId = getCurrentUserIdFromCache();
      try {
        const statusRes = await axios.get(`${API}/vault/status`, { withCredentials: true });
        userId = statusRes.data.user_id || userId;
      } catch {}

      console.log('[biometric] vault enroll start', {
        isIosDespia: isIosDespiaRuntime(),
        ua: navigator.userAgent,
        origin: window.location.origin,
      });

      if (isIosDespiaRuntime()) {
        // Native bridge path — Face ID / Touch ID on iOS Despia only.
        // WebAuthn is not exposed inside iOS WKWebView so this is the only
        // way Face ID can reach the JS layer on iOS. Android Despia uses
        // the standard WebAuthn path (Chromium WebView exposes
        // navigator.credentials normally).
        await enrollDespiaBioauth(userId);
      } else {
        const pageOrigin = window.location.origin;
        const optRes = await axios.post(`${API}/vault/webauthn/register-options`, { origin: pageOrigin }, { withCredentials: true });
        const credential = await registerBiometric(optRes.data);
        let deviceLabel = '';
        try {
          const ua = (navigator.userAgent || '').toLowerCase();
          if (ua.includes('android')) deviceLabel = '';
        } catch {}
        await axios.post(`${API}/vault/webauthn/register`, { ...credential, origin: pageOrigin, device_label: deviceLabel }, { withCredentials: true });
        markDeviceEnrolled(userId);
      }

      setVaultHasBiometric(true);
      setVaultModalVisible(false);
      toast.success('Biometric unlock enabled on this device!');
    } catch (err) {
      const name = err?.name;
      const msg = String(err?.message || '');
      const serverMsg = err?.response?.data?.detail;
      console.error('[biometric] vault enroll failed', { name, msg, serverMsg, err });
      if (name === 'NotAllowedError' || msg.includes('cancelled')) {
        toast.error('Biometric prompt was cancelled or denied');
      } else if (name === 'SecurityError') {
        toast.error('Security error — site origin does not match. Try reopening the app.');
      } else if (name === 'InvalidStateError') {
        toast.error('This device is already enrolled — try unlocking instead.');
      } else if (name === 'NotSupportedError') {
        toast.error('Your device does not support biometric unlock');
      } else if (name === 'AbortError' || msg.includes('timeout')) {
        toast.error('Biometric prompt timed out — please try again');
      } else if (msg.includes('unavailable')) {
        toast.error('Biometric is not configured on this device. Set up Face ID / Touch ID / fingerprint, then retry.');
      } else if (msg.includes('bridge_load_failed') || msg.includes('not_despia')) {
        toast.error('Biometric bridge unavailable — please update the app');
      } else if (serverMsg) {
        toast.error(`Server: ${serverMsg}`);
      } else {
        toast.error(`Biometric setup failed: ${msg || name || 'unknown error'}`);
      }
      throw err;
    }
  };

  return {
    vaultModalVisible,
    setVaultModalVisible,
    vaultModalMode,
    vaultPinLoading,
    vaultHasBiometric,
    biometricCapable,
    sidebarDefaultTab,
    setSidebarDefaultTab,
    handleVaultRequest,
    handleVaultPinVerify,
    handleVaultPinSetup,
    handleVaultBiometricAuth,
    handleVaultBiometricEnroll,
  };
}
